/**
 * ABMHCFeaturedMedia — public hero video section.
 *
 * Zero hardcoded video content. Fetches the currently-active row from
 * `GET /api/public/abmhc/featured-media` and renders it via a safe
 * provider-aware player (YouTube nocookie, Vimeo, HTML5 <video>, HLS).
 * When nothing is published (or the row is disabled) the component
 * renders NOTHING — no empty space.
 *
 * All CTA clicks + player events fire `/api/public/abmhc/featured-media/{id}/event`
 * so the CMS analytics widget stays live.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ABMHCFeaturedMedia({ className = '' }) {
  const [media, setMedia] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/api/public/abmhc/featured-media`)
      .then(r => {
        setEnabled(!!r.data?.enabled);
        setMedia(r.data?.media || null);
      })
      .catch(() => { setEnabled(false); setMedia(null); })
      .finally(() => setLoaded(true));
  }, []);

  const track = (kind, extra = {}) => {
    if (!media?.id) return;
    axios.post(`${API}/api/public/abmhc/featured-media/${media.id}/event`,
      { kind, ...extra }).catch(() => {});
  };

  if (!loaded || !enabled || !media) return null;

  const embed = media.embed || {};
  const donateHref = media.donation_url || '/campaigns/abmhc';
  const projectHref = media.project_url || '/campaigns/abmhc';

  return (
    <section
      id="abmhc-featured-media"
      className={`featured-media-section w-full py-12 md:py-20 bg-gradient-to-br from-indigo-950 via-slate-900 to-slate-950 text-white ${className}`}
      data-testid="abmhc-featured-media"
    >
      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="featured-media-copy">
          {media.label && (
            <p className="text-xs uppercase tracking-widest text-amber-300 mb-3"
                data-testid="abmhc-label">{media.label}</p>
          )}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight mb-4"
              data-testid="abmhc-title">
            {media.section_title || 'Hope Is Rising in Zimbabwe'}
          </h2>
          {media.description && (
            <p className="text-base md:text-lg text-slate-300 mb-6 whitespace-pre-line"
                data-testid="abmhc-description">
              {media.description}
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <a href={donateHref}
                onClick={() => track('donate')}
                className="inline-flex items-center bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-6 py-3 rounded-full transition"
                data-testid="abmhc-cta-primary">
              {media.cta_primary_label || 'Support the Vision'}
            </a>
            <a href={projectHref}
                onClick={() => track('project')}
                className="inline-flex items-center border border-white/30 hover:border-white text-white px-6 py-3 rounded-full transition"
                data-testid="abmhc-cta-secondary">
              {media.cta_secondary_label || 'View Project Progress'}
            </a>
            <button
              onClick={() => {
                track('share');
                if (navigator.share) {
                  navigator.share({
                    title: media.section_title,
                    text: media.description,
                    url: window.location.href,
                  }).catch(() => {});
                } else if (navigator.clipboard) {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
              className="inline-flex items-center border border-white/20 hover:border-white/40 text-white/70 hover:text-white px-4 py-3 rounded-full transition text-sm"
              data-testid="abmhc-share"
            >
              Share
            </button>
          </div>
        </div>

        <div className="featured-media-player">
          <div className="responsive-video-container relative aspect-video rounded-xl overflow-hidden shadow-2xl bg-black"
                onMouseEnter={() => {
                  if (!startedRef.current) { startedRef.current = true; track('start'); }
                }}>
            <EmbedPlayer embed={embed} title={media.video_title || media.section_title}
                            poster={media.thumbnail_url}
                            captionsUrl={media.captions_url} />
            {embed.error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-center p-4">
                <p className="text-sm text-slate-300 mb-3">Video temporarily unavailable</p>
                <a href={media.video_url || projectHref}
                    className="text-amber-300 underline text-sm"
                    data-testid="abmhc-fallback-link">
                  Watch on Faith Works TV →
                </a>
              </div>
            )}
          </div>
          {media.video_title && (
            <p className="text-sm text-slate-400 mt-3 text-center md:text-left"
                data-testid="abmhc-video-title">{media.video_title}</p>
          )}
        </div>
      </div>
    </section>
  );
}


function EmbedPlayer({ embed, title, poster, captionsUrl }) {
  const kind = embed?.kind;
  const src = embed?.embed_url;
  if (embed?.error || !src) {
    // Fallback poster with no interactive iframe.
    return (
      <img src={poster || '/abmhc-fallback-poster.jpg'} alt={title}
             className="w-full h-full object-cover" />
    );
  }
  if (kind === 'iframe') {
    return (
      <iframe
        src={src}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        className="absolute inset-0 w-full h-full border-0"
        data-testid="abmhc-iframe"
      />
    );
  }
  if (kind === 'video' || kind === 'hls') {
    return (
      <video
        controls
        poster={poster || undefined}
        preload="metadata"
        className="w-full h-full object-cover"
        data-testid="abmhc-video-el"
      >
        <source src={src} type={kind === 'hls' ? 'application/vnd.apple.mpegurl' : 'video/mp4'} />
        {captionsUrl && <track src={captionsUrl} kind="captions" srcLang="en" label="English" default />}
        Your browser does not support HTML5 video.
      </video>
    );
  }
  if (kind === 'internal') {
    return (
      <a href={src} className="absolute inset-0 flex items-center justify-center bg-black/60">
        <div className="text-center">
          <p className="text-4xl mb-2">▶</p>
          <p className="text-sm">Open on Faith Works TV</p>
        </div>
      </a>
    );
  }
  return null;
}
